import { Component, computed, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule, MatSelectTrigger } from '@angular/material/select';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ICampaignForm } from '@campaign-form/campaign-form.interface';
import { CampaignFormStore } from '@campaign-form/campaign-form.store';
import { ITeam } from '@core/interfaces/team.interface';
import { IObjectPermission, IUser } from '@core/interfaces/user.interface';
import { AuthService } from '@core/services/auth.service';
import { PermissionService } from '@core/services/permission.service';
import { EPermissions } from '@enums/permissions.enum';
import { ERoles } from '@enums/roles.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { debounceTime } from 'rxjs/operators';
import { idFor, parseEntryId } from './campaign-team-entry.util';
import { IVisibilityEntry, TEntryKind } from './campaign-team.interface';
import { CampaignTeamStore } from './campaign-team.store';

/**
 * "Visibility" section of the campaign Basics form - controls `ICampaignForm.whitelist`
 * (`IObjectPermission[]`), i.e. who besides the campaign's own creator can see it: nobody
 * (toggle off, everyone in the organization can view it), or an explicit mix of individual users
 * and whole teams (toggle on). Unrelated to CampaignTeamSelectComponent (the "Team" field above
 * this one in the Basics form) - that one assigns the campaign to a single organizational team
 * purely for the campaigns list filter; this one is about *who can see* the campaign at all, and
 * can include any number of users and/or teams.
 *
 * If the *current* user has the `ERoles.admin` role (Shikenso's own internal/platform-level
 * accounts, not the group-scoped role also displayed as "Admin" for regular customer users), they
 * never appear as an option here themselves - see `entries`' doc comment. That's also why the
 * default selection a freshly enabled toggle starts with depends on the current user's own role:
 * a non-admin current user is defaulted to themselves and can't remove that (turning visibility on
 * without including yourself would immediately lock you out of a campaign you're looking at),
 * while an admin current user - who can't be an option themselves - is defaulted to the first
 * available option instead, and *can* remove it; removing the last remaining option turns
 * visibility back off entirely rather than leaving it enabled with nothing selected (see
 * `normalizeSelectedEntryIds`). See `hasSelectableOptions` for what happens when an admin current
 * user has nothing else to default to at all.
 */
@Component({
  imports: [
    IconBadgeComponent,
    IconComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatOptionModule,
    MatSelectModule,
    MatSelectTrigger,
    MatSlideToggleModule,
    MatTooltipModule,
    NgxMatSelectSearchModule,
    NgxSkeletonLoaderModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  providers: [CampaignTeamStore],
  selector: 'app-campaign-team',
  templateUrl: './campaign-team.component.html',
})
export class CampaignTeamComponent {
  public readonly form = input.required<FormGroup<ICampaignForm>>();

  public readonly currentUser: IUser | null = AuthService.currentDbUser;
  public readonly searchTerm = signal<string>('');
  public readonly visibilityEnabled = signal<boolean>(false);

  public readonly entrySearchControl: FormControl<string | null> = new FormControl<string | null>('');
  public readonly selectedEntryIdsControl: FormControl<string[]> = new FormControl<string[]>([], { nonNullable: true });
  public readonly optionSkeletonIndexes: number[] = [0, 1, 2, 3, 4, 5];

  /** Mirrors `selectedEntryIdsControl`'s value, written directly by `setSelection` (the control's
   * only writer) rather than derived from `selectedEntryIdsControl.valueChanges` - `setSelection`
   * writes with `emitEvent: false` (to avoid re-entering `subscribeToSelectionChanges`), so a
   * signal derived from `valueChanges` would never reflect a selection seeded from the form (e.g.
   * on initial load of a campaign that already has a whitelist) and would only start working after
   * the user manually toggled an option. `entries`/`selectedEntries`/`displayedEntries` all read
   * this signal, so this is what makes them - and the whitelist pills - actually reflect the
   * campaign's existing visibility list as soon as it loads.
   *
   * Uses a content-based `equal` (rather than the default reference equality) because `entries`
   * itself reads this signal to build its placeholder entries, and `createEntriesLoadedEffect`
   * reads `entries()` and, in reaction, calls `setSelection` again with (typically) the same ids -
   * needed so `MatSelect` re-resolves its selection once real options load, NOT a no-op to skip.
   * `setSelection` still unconditionally re-runs `selectedEntryIdsControl.setValue(...)` every
   * time (so that re-sync keeps happening), but without content-based equality here, writing the
   * *same* ids back into this signal would still count as "changed" (a new array reference),
   * re-triggering `entries` and therefore that same effect - forever. Comparing by content makes
   * repeated same-ids writes a true no-op for this signal specifically, breaking that loop while
   * leaving every other call site (and the FormControl sync) untouched. */
  private readonly selectedEntryIdsSignal = signal<string[]>([], {
    equal: (a, b) => a.length === b.length && a.every((id, index) => id === b[index]),
  });
  public readonly selectedEntryIds = this.selectedEntryIdsSignal.asReadonly();

  /** Teams come from the shared `CampaignFormStore` (provided on the ancestor
   * CampaignFormComponent) rather than this component's own `CampaignTeamStore` - see that
   * store's file-level doc comment for why: fetching them here too would duplicate the `GET
   * /teams` request CampaignBasicsComponent's `createFetchEffects()` already triggers.
   * `campaignFormStore.teams$()` stays `null` forever when `canReadTeams()` is false (that
   * effect never fires) - gating the teams half on that permission keeps this from getting stuck
   * `true` forever instead of resolving once the users fetch (which always happens) settles. */
  public readonly loading = computed<boolean>(
    () =>
      this.store.loading$() ||
      this.store.users$() === null ||
      (this.canReadTeams() && (this.campaignFormStore.teamsLoading$() || this.campaignFormStore.teams$() === null)),
  );

  /** Every user and team the current user can grant visibility to, unified into one list - sorted
   * within `sortEntriesByName` (current user always first, then every team alphabetically, then
   * every other user alphabetically). The current user's own entry is left out entirely
   * when *they* are an admin (see `isCurrentUserAdmin`) - the backend rejects a whitelist entry
   * for one, since an admin isn't a member of any customer's user group. Deliberately scoped to
   * just the current user rather than every admin in the list - the general user list here isn't
   * fetched with the join that would populate every other user's own roles reliably, so their
   * admin status isn't something this component can check; only the signed-in user's own role
   * (via `PermissionService`) is trustworthy.
   *
   * Also synthesizes a placeholder entry for any currently-selected id that doesn't resolve to a
   * real user/team here - most commonly a team the current user can't read (missing
   * `EPermissions.teamReadOwn`) but which is already in the campaign's whitelist, though the same
   * applies to any id that no longer resolves (e.g. a deleted team). This isn't just cosmetic:
   * `displayedEntries` (and therefore the rendered `<mat-option>`s) is built from `entries`, and
   * `MatSelect` only ever keeps an id "selected" internally if it matches one of its rendered
   * options - an id with no corresponding option is silently dropped from the value `MatSelect`
   * emits the next time the user toggles ANY other option in this select, which would silently
   * strip that team from `whitelist` on save. Giving it a real (if generic) placeholder option
   * keeps it selectable/matched and prevents that data loss, in addition to fixing the display. */
  public readonly entries = computed<IVisibilityEntry[]>(() => {
    const users: IUser[] = this.store.users$() ?? [];
    const teams: ITeam[] = this.campaignFormStore.teams$() ?? [];

    const userEntries: IVisibilityEntry[] = users
      .filter((user) => !this.isCurrentUserAdmin() || user.id !== this.currentUser?.id)
      .map((user) => ({
        iconId: user.iconId ?? null,
        id: user.id,
        kind: 'user',
        name: user.name ?? '',
      }));
    const teamEntries: IVisibilityEntry[] = teams.map((team) => ({
      iconId: team.iconId ?? null,
      id: team.id,
      kind: 'team',
      name: team.name,
    }));

    const knownEntries = [...userEntries, ...teamEntries];
    const knownEntryIds = new Set(knownEntries.map((entry) => idFor(entry.kind, entry.id)));

    const placeholderEntries: IVisibilityEntry[] = this.selectedEntryIds()
      .filter((entryId) => !knownEntryIds.has(entryId))
      .map((entryId) => parseEntryId(entryId))
      .filter((parsed): parsed is { kind: TEntryKind; id: string } => parsed !== null)
      .map((parsed) => ({
        iconId: null,
        id: parsed.id,
        kind: parsed.kind,
        name: this.translate.instant(
          parsed.kind === 'team'
            ? 'pages.campaignForm.sections.basics.teamsUnknownTeam'
            : 'pages.campaignForm.sections.basics.teamsUnknownUser',
        ),
      }));

    return this.sortEntriesByName([...knownEntries, ...placeholderEntries]);
  });

  /** Whether the toggle can be enabled at all - only false for an admin current user when there's
   * truly nothing else to select (no other users, no teams): they're the only case where
   * `entries` can end up empty (every other role always has themselves). Checked against the raw
   * users$()/teams$() lists rather than `entries()` itself, since `entries` can also hold
   * placeholder entries for stale selected ids that shouldn't count as "a real option exists".
   * Drives the toggle's `disabled`/tooltip binding in the template - `loading()` is checked
   * alongside it there so the tooltip doesn't flash before the lists have even arrived. */
  public readonly hasSelectableOptions = computed<boolean>(() => {
    const users: IUser[] = this.store.users$() ?? [];
    const teams: ITeam[] = this.campaignFormStore.teams$() ?? [];
    const hasOtherUsers = users.some((user) => !this.isCurrentUserAdmin() || user.id !== this.currentUser?.id);

    return hasOtherUsers || teams.length > 0;
  });

  public readonly selectedEntries = computed<IVisibilityEntry[]>(() => {
    const selectedIds = new Set(this.selectedEntryIds());
    this.form();

    return this.sortEntriesByName(this.entries().filter((entry) => selectedIds.has(idFor(entry.kind, entry.id))));
  });

  public readonly displayedEntries = computed<IVisibilityEntry[]>(() => {
    this.form();
    const term = this.searchTerm().trim().toLowerCase();
    const selectedIds = new Set(this.selectedEntryIds());
    const orderedSelectedEntries = this.sortEntriesByName(
      this.entries().filter((entry) => selectedIds.has(idFor(entry.kind, entry.id))),
    );
    const filteredEntries = this.filterEntriesBySearch(this.entries(), term);
    const orderedUnselectedEntries = this.sortEntriesByName(
      filteredEntries.filter((entry) => !selectedIds.has(idFor(entry.kind, entry.id))),
    );

    if (term) {
      return [...orderedUnselectedEntries, ...orderedSelectedEntries];
    }

    return [...orderedSelectedEntries, ...orderedUnselectedEntries];
  });

  public readonly skeletonPillIndexes = computed<number[]>(() => {
    const count = this.getWhitelistEntryIdsFromForm().length || 2;

    return Array.from({ length: count }, (_, index: number) => index);
  });

  public readonly store = inject(CampaignTeamStore);
  private readonly campaignFormStore = inject(CampaignFormStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly permissionService = inject(PermissionService);
  private readonly translate = inject(TranslateService);

  private readonly currentUserPermissions = toSignal(this.permissionService.currentUserPermissions$, {
    initialValue: this.permissionService.currentUserPermissions,
  });

  public readonly canReadTeams = computed(() => this.currentUserPermissions().includes(EPermissions.teamReadOwn));

  private isSyncing = false;

  constructor() {
    this.store.ensureUsersLoaded();

    // No teams fetch here - CampaignBasicsComponent's createFetchEffects() already triggers
    // CampaignFormStore.fetchTeams() for the whole form (see the `loading`/`entries` doc comments
    // above), so firing another one here would duplicate that request.

    this.subscribeToSearchChanges();
    this.subscribeToSelectionChanges();
    this.createWhitelistSyncEffect();
    this.createEntriesLoadedEffect();
  }

  public onVisibilityToggleChange(event: MatSlideToggleChange): void {
    this.visibilityEnabled.set(event.checked);

    if (!event.checked) {
      this.setSelection([]);
      this.updateWhitelist([]);

      return;
    }

    const selectedEntryIds = this.getDefaultSelection();

    this.setSelection(selectedEntryIds);
    this.updateWhitelist(selectedEntryIds);
  }

  public onRemoveEntry(entry: IVisibilityEntry, event: Event): void {
    event?.stopPropagation();

    if (this.isCurrentUserEntry(entry)) {
      return;
    }

    const selectedEntryIds = (this.selectedEntryIdsControl.value ?? []).filter(
      (id) => id !== idFor(entry.kind, entry.id),
    );
    const normalizedEntryIds = this.normalizeSelectedEntryIds(selectedEntryIds);

    this.setSelection(normalizedEntryIds);
    this.updateWhitelist(normalizedEntryIds);
  }

  public isCurrentUserEntry(entry: IVisibilityEntry): boolean {
    return entry.kind === 'user' && this.isCurrentUserId(entry.id);
  }

  /** The exact control value a given entry maps to - used by the template for `[value]` on each
   * `mat-option`, since `idFor` itself is a private module-level helper. */
  public entryId(entry: IVisibilityEntry): string {
    return idFor(entry.kind, entry.id);
  }

  private createWhitelistSyncEffect(): void {
    effect(() => {
      const whitelistEntryIds = this.getWhitelistEntryIdsFromForm();
      const hasWhitelist = whitelistEntryIds.length > 0;

      this.visibilityEnabled.set(hasWhitelist);
      this.setSelection(this.getSortedEntryIdsByName(whitelistEntryIds));
    });
  }

  private createEntriesLoadedEffect(): void {
    effect(() => {
      const entries = this.entries();

      if (!entries.length) {
        return;
      }

      const selectedIds = this.getSortedEntryIdsByName(this.selectedEntryIdsControl.value ?? []);

      this.setSelection(selectedIds);
      this.updateWhitelist(selectedIds, false);
    });
  }

  private subscribeToSearchChanges(): void {
    this.entrySearchControl.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe((searchValue: string | null) => {
        this.searchTerm.set(searchValue ?? '');
      });
  }

  private subscribeToSelectionChanges(): void {
    this.selectedEntryIdsControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selectedEntryIds: string[]) => {
        if (this.isSyncing) {
          return;
        }

        const normalizedEntryIds = this.normalizeSelectedEntryIds(selectedEntryIds);

        this.setSelection(normalizedEntryIds);
        this.updateWhitelist(normalizedEntryIds);
      });
  }

  private normalizeSelectedEntryIds(selectedEntryIds: string[]): string[] {
    const uniqueEntryIds = Array.from(new Set(selectedEntryIds)).filter(Boolean);

    if (!this.visibilityEnabled()) {
      return [];
    }

    const ensuredEntryIds = this.ensureCurrentUserSelected(uniqueEntryIds);

    if (!ensuredEntryIds.length) {
      // Only reachable for an admin current user - every other role always has themselves
      // included via ensureCurrentUserSelected below, so their selection can never actually go
      // empty. Deselecting the last remaining option turns visibility off entirely instead of
      // leaving the toggle on with nothing selected.
      this.visibilityEnabled.set(false);

      return [];
    }

    return this.getSortedEntryIdsByName(ensuredEntryIds);
  }

  /** The selection a freshly enabled toggle starts with - see the class doc comment for why this
   * differs by role. `entries()[0]` is never the current user for an admin (they're never an
   * option - see `entries`' doc comment), so there's no risk of re-selecting the same id both
   * ways. */
  private getDefaultSelection(): string[] {
    if (this.currentUser?.id && !this.isCurrentUserAdmin()) {
      return [idFor('user', this.currentUser.id)];
    }

    const [firstEntry] = this.entries();

    return firstEntry ? [this.entryId(firstEntry)] : [];
  }

  private ensureCurrentUserSelected(selectedEntryIds: string[]): string[] {
    const currentUserId = this.currentUser?.id;

    if (!currentUserId || this.isCurrentUserAdmin()) {
      return selectedEntryIds;
    }

    const currentUserEntryId = idFor('user', currentUserId);

    if (!selectedEntryIds.includes(currentUserEntryId)) {
      return [currentUserEntryId, ...selectedEntryIds];
    }

    return selectedEntryIds;
  }

  /** `ERoles.admin` (Shikenso's own internal/platform-level admin accounts) - deliberately NOT
   * the group-scoped built-in role that's *also* displayed as "Admin" for regular customer users
   * (see role-name.util.ts's doc comment). Delegates to `PermissionService.hasRole` (an existing,
   * already-established check, `.some()` over the full `roles` array) rather than re-deriving
   * this by hand - a user's `roles` can hold more than one entry at once, so checking only the
   * last one would risk missing an admin who also happens to carry a trailing group-role entry. */
  private isCurrentUserAdmin(): boolean {
    return this.permissionService.hasRole(ERoles.admin);
  }

  private setSelection(selectedEntryIds: string[]): void {
    this.isSyncing = true;
    this.selectedEntryIdsControl.setValue(selectedEntryIds, { emitEvent: false });
    this.selectedEntryIdsSignal.set(selectedEntryIds);

    if (!selectedEntryIds.length) {
      this.selectedEntryIdsControl.disable();
    } else {
      this.selectedEntryIdsControl.enable();
    }

    this.isSyncing = false;
  }

  private updateWhitelist(selectedEntryIds: string[], markAsDirty = true): void {
    const existingWhitelist = this.form().controls.whitelist.value ?? [];
    const idByUserId = new Map(
      existingWhitelist.filter((entry) => entry.userId).map((entry) => [entry.userId, entry.id]),
    );
    const idByTeamId = new Map(
      existingWhitelist.filter((entry) => entry.teamId).map((entry) => [entry.teamId, entry.id]),
    );

    const whitelist: IObjectPermission[] = selectedEntryIds.reduce<IObjectPermission[]>((accumulator, entryId) => {
      const parsed = parseEntryId(entryId);

      if (!parsed) {
        return accumulator;
      }

      if (parsed.kind === 'user') {
        accumulator.push({
          ...(idByUserId.get(parsed.id) ? { id: idByUserId.get(parsed.id) } : {}),
          userId: parsed.id,
        });
      } else {
        accumulator.push({
          ...(idByTeamId.get(parsed.id) ? { id: idByTeamId.get(parsed.id) } : {}),
          teamId: parsed.id,
        });
      }

      return accumulator;
    }, []);

    this.form().controls.whitelist.setValue(whitelist);

    if (markAsDirty) {
      this.form().controls.whitelist.markAsDirty();
    }
  }

  private filterEntriesBySearch(entries: IVisibilityEntry[], term: string): IVisibilityEntry[] {
    if (!term) {
      return entries;
    }

    return entries.filter((entry) => entry.name.toLowerCase().includes(term));
  }

  private getSortedEntryIdsByName(entryIds: string[]): string[] {
    const entriesById = new Map<string, IVisibilityEntry>(
      this.entries().map((entry) => [idFor(entry.kind, entry.id), entry]),
    );
    const currentUserEntryId = this.currentUser?.id ? idFor('user', this.currentUser.id) : null;

    return [...entryIds].sort((a, b) => {
      if (currentUserEntryId && a === currentUserEntryId) {
        return -1;
      }

      if (currentUserEntryId && b === currentUserEntryId) {
        return 1;
      }

      const nameA = entriesById.get(a)?.name ?? '';
      const nameB = entriesById.get(b)?.name ?? '';

      return nameA.localeCompare(nameB);
    });
  }

  /** Reads `IObjectPermission[]` off the form and turns each entry back into a prefixed
   * `user:`/`team:` control value - the inverse of updateWhitelist's own mapping. An entry with
   * neither `userId` nor `teamId` (shouldn't happen, but the field went from required to optional
   * on IObjectPermission) is silently dropped rather than crashing the sync. */
  private getWhitelistEntryIdsFromForm(): string[] {
    const whitelist = this.form().controls.whitelist.value ?? [];

    return whitelist
      .map((entry) => (entry.userId ? idFor('user', entry.userId) : entry.teamId ? idFor('team', entry.teamId) : null))
      .filter((entryId): entryId is string => entryId !== null);
  }

  private isCurrentUserId(userId: string): boolean {
    return this.currentUser?.id === userId;
  }

  /** Current user always pinned first (see `entries`' doc comment), then every team ahead of
   * every user - each of those two groups ordered alphabetically ascending on its own, never
   * interleaved by name across kinds. */
  private sortEntriesByName(entries: IVisibilityEntry[]): IVisibilityEntry[] {
    const currentUserId = this.currentUser?.id ?? null;

    return [...entries].sort((a, b) => {
      const aIsCurrentUser = !!currentUserId && a.kind === 'user' && a.id === currentUserId;
      const bIsCurrentUser = !!currentUserId && b.kind === 'user' && b.id === currentUserId;

      if (aIsCurrentUser || bIsCurrentUser) {
        return aIsCurrentUser === bIsCurrentUser ? 0 : aIsCurrentUser ? -1 : 1;
      }

      if (a.kind !== b.kind) {
        return a.kind === 'team' ? -1 : 1;
      }

      return a.name.localeCompare(b.name);
    });
  }
}
